import wrap from 'word-wrap';
import { formatDateTime } from '@lib/utils';
import type { CongressBase, Person, VotingCode } from '@domain/index';
// import type { Person, VotingCode } from '../types'

const INIT = '\x1b\x40';
const ALIGN_LEFT = '\x1b\x61\x00';
const ALIGN_CENTER = '\x1b\x61\x01';
const DOUBLE_SIZE = '\x1d\x21\x11';
const TRIPLE_SIZE = '\x1d\x21\x22';
const NORMAL_SIZE = '\x1d\x21\x00';
const BOLD_ON = '\x1b\x45\x01';
const BOLD_OFF = '\x1b\x45\x00';
const INVERT_ON = '\x1d\x42\x01';
const INVERT_OFF = '\x1d\x42\x00';
const HLINE = '─'.repeat(42);
const SKIP4 = '\x1b\x64\x04';
const SKIP8 = '\x1b\x64\x08';
const CUT = `${SKIP8}\x1d\x56\x00`;

const header = (congressName: string): string =>
  `${INIT}${ALIGN_CENTER}${BOLD_ON}${wrap(congressName.toUpperCase(), { width: 20, indent: '' })}${BOLD_OFF}\n${ALIGN_LEFT}`;

const signLine = `${SKIP4}${HLINE}\nSign here\n\n`;

function personInfo(person: Person): string {
  const name = `${person.firstname} ${person.lastname}`;

  let string = `${DOUBLE_SIZE}${wrap(name, { width: 20, indent: '' })}\n`;
  string += `${wrap(person.personType.name, { width: 20, indent: '' })}${NORMAL_SIZE}\n\n`;
  string += 'Ticket number:\n';
  string += `${DOUBLE_SIZE}${INVERT_ON}${person.ticketnumber}${INVERT_OFF}\n`;
  string += `${NORMAL_SIZE}\n`;

  return string;
}

const footer = (): string => `Printed at ${formatDateTime(new Date())}\n${CUT}`;

export async function registrationReceipt(
  congress: CongressBase,
  person: Person
): Promise<void> {
  const { printReceipt } = await import('@lib/receipts/printer');

  let string = `${header(congress.name)}${ALIGN_CENTER}Registration\n\n`;
  string += `${personInfo(person)}${signLine}${footer()}`;

  await printReceipt(string);
}

export async function nameSlip(
  congress: CongressBase,
  person: Person
): Promise<void> {
  const { printReceipt } = await import('@lib/receipts/printer');

  let string = `${header(congress.name)}\n\n`;
  string += `${ALIGN_CENTER}${personInfo(person)}${footer()}`;

  await printReceipt(string);
}

function qrCode(data: string): string {
  let string = '\x0a';
  // set QR code to model 2
  string += '\x1d\x28\x6b\x04\x00\x31\x41\x32\x00';
  // set QR code size to 6
  string += '\x1d\x28\x6b\x03\x00\x31\x43\x08';
  // set QR code error correction to M
  string += '\x1d\x28\x6b\x03\x00\x31\x45\x31';

  // write the data
  const length = data.length + 3;
  string += `\x1d\x28\x6b${String.fromCharCode(length % 0xff, length / 0xff)}\x31\x50\x30${data}`;

  // print the QR code
  string += '\x1d\x28\x6b\x03\x00\x31\x51\x30';
  string += '\x0a';

  return string;
}

export async function votingCodeSlip(
  congress: CongressBase,
  person: Person,
  votingCodes: VotingCode[]
): Promise<void> {
  const { printReceipt } = await import('@lib/receipts/printer');
  const name = `${person.firstname} ${person.lastname}`;

  let string = `${header(congress.name)}${ALIGN_CENTER}Leadership Voting\n\n`;
  string += `${DOUBLE_SIZE}${wrap(name, { width: 20, indent: '' })}\n\n${NORMAL_SIZE}`;

  for (const votingCode of votingCodes) {
    string += `${BOLD_ON}${votingCode.election.name}${BOLD_OFF}\n\n`;
    string += 'Your voting code is:\n\n';
    string += `${TRIPLE_SIZE}${votingCode.code}${NORMAL_SIZE}\n\n\n`;

    const link = votingCode.election.votingLink;

    if (link != null) {
      string += 'Scan the QR code to vote:\n';
      string += qrCode(link);
      string += 'or go to the following link:\n\n';
      string += `${link}\n\n`;
      string += 'or use one of the voting stations.\n\n\n';
    }
  }

  string += footer();

  await printReceipt(string);
}

export async function printVoucher(
  congress: CongressBase,
  person: Person,
  voucherFor: string,
  reprint: boolean = false
): Promise<void> {
  const { printReceipt } = await import('@lib/receipts/printer');

  let string = `${header(congress.name)}${ALIGN_CENTER}Voucher\n\n`;
  string += `${TRIPLE_SIZE}${wrap(voucherFor, { width: 15, indent: '' })}\n\n${NORMAL_SIZE}`;
  if (reprint) {
    string += `${DOUBLE_SIZE}${INVERT_ON}${BOLD_ON}REPRINT${BOLD_OFF}${INVERT_OFF}\n\n${NORMAL_SIZE}`;
  }
  string += `${personInfo(person)}\n\n`;

  string += footer();

  await printReceipt(string);
}

export async function printerTest(congress: CongressBase): Promise<void> {
  const { printReceipt } = await import('@lib/receipts/printer');

  let string = header(congress.name);
  string += `${ALIGN_CENTER}Printer Test\n\n${ALIGN_LEFT}`;

  string += 'Printer test succeeded\n\n';
  string += footer();

  await printReceipt(string);
}
